/**
 * The device database: everything offline mode keeps between page loads.
 *
 * WHY RAW INDEXEDDB AND NOT DEXIE. `pnpm` is not reliably on PATH in this repo's
 * environments, and `pnpm install` reruns `postinstall` (which rewrites
 * `iconify/icons.css` and `public/mockServiceWorker.js`), so adding a dependency
 * is a bigger and riskier change than the ~200 lines below. The app already
 * talks to raw IndexedDB in `layouts/components/AppClearCacheButton.vue`.
 *
 * WHY INDEXEDDB AND NOT localStorage. Unsent sales and clinical notes must
 * survive a browser crash, a reboot and a flat battery, and must hold megabytes.
 * `localStorage` is synchronous (it would stall the till on every write), capped
 * at ~5 MB, and `docs/README.frontend-caching-plan.md` §11 bans backend rows in
 * it. IndexedDB survives all three events and is available on plain HTTP, which
 * a service worker is not.
 *
 * DURABILITY. IndexedDB's default write durability is "relaxed": the browser may
 * acknowledge a transaction before the OS has flushed it, so a POWER CUT (not a
 * tab crash) can lose the last few writes. Anything we would be sorry to lose —
 * the outbox above all — is written with `durability: "strict"`. It is slower,
 * which is exactly why it is opt-in per call rather than the default.
 */

export const OFFLINE_DB_NAME = "alkokh-offline";
export const OFFLINE_DB_VERSION = 1;

export const STORE_PACKS = "packs";
export const STORE_OUTBOX = "outbox";
export const STORE_DRAFTS = "drafts";
export const STORE_META = "meta";

export type OfflineStoreName =
  | typeof STORE_PACKS
  | typeof STORE_OUTBOX
  | typeof STORE_DRAFTS
  | typeof STORE_META;

/**
 * Raised when the device store cannot be used at all (private mode, blocked
 * storage, a browser with IndexedDB disabled).
 *
 * This is deliberately a hard failure that callers must handle rather than a
 * silent no-op. Eight existing storage sites in this app swallow write errors,
 * which is right for a remembered column width and catastrophic for a sale: the
 * cashier would be told the sale was saved when nothing was written. The rule
 * for this module is the one `useStockTransfer.ts` already follows — refuse the
 * action and say so.
 */
export class OfflineStorageError extends Error {
  readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "OfflineStorageError";
    this.cause = cause;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function indexedDbFactory(): IDBFactory | null {
  if (typeof window === "undefined")
    return null;

  try {
    return window.indexedDB ?? null;
  }
  catch {
    // Accessing `indexedDB` itself throws in some locked-down configurations.
    return null;
  }
}

export function isOfflineStorageSupported(): boolean {
  return indexedDbFactory() !== null;
}

function upgrade(db: IDBDatabase) {
  // Schema changes are additive only. Never drop a store on upgrade: the outbox
  // may hold a day of unsent sales belonging to a user who is not even at the
  // device right now.
  if (!db.objectStoreNames.contains(STORE_PACKS)) {
    // key: `<user>:<pack>:<dataset>`
    const packs = db.createObjectStore(STORE_PACKS, { keyPath: "key" });

    packs.createIndex("user", "user", { unique: false });
    packs.createIndex("pack", "pack", { unique: false });
  }

  if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
    const outbox = db.createObjectStore(STORE_OUTBOX, { keyPath: "id" });

    // Draining is "oldest pending first", and the queue screen filters by state.
    outbox.createIndex("status", "status", { unique: false });
    outbox.createIndex("createdAt", "createdAt", { unique: false });
    outbox.createIndex("user", "user", { unique: false });
    // An idempotency key must never appear twice: that is the whole guarantee
    // against double-charging a customer, so the DATABASE enforces it rather
    // than trusting every future call site to check first.
    outbox.createIndex("idempotencyKey", "idempotencyKey", { unique: true });
  }

  if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
    // key: `<user>:<screen>:<slot>`
    const drafts = db.createObjectStore(STORE_DRAFTS, { keyPath: "key" });

    drafts.createIndex("user", "user", { unique: false });
    drafts.createIndex("updatedAt", "updatedAt", { unique: false });
  }

  if (!db.objectStoreNames.contains(STORE_META))
    db.createObjectStore(STORE_META, { keyPath: "key" });
}

export function openOfflineDb(): Promise<IDBDatabase> {
  if (dbPromise)
    return dbPromise;

  const factory = indexedDbFactory();

  if (!factory) {
    return Promise.reject(
      new OfflineStorageError("This browser has no usable local storage, so offline mode cannot run here."),
    );
  }

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    let request: IDBOpenDBRequest;

    try {
      request = factory.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    }
    catch (error) {
      reject(new OfflineStorageError("Could not open the device database.", error));

      return;
    }

    request.onupgradeneeded = () => {
      try {
        upgrade(request.result);
      }
      catch (error) {
        reject(new OfflineStorageError("Could not prepare the device database.", error));
      }
    };

    request.onsuccess = () => {
      const db = request.result;

      // Another tab asked for a version we cannot serve. Close so its upgrade is
      // not blocked; the next call reopens at the new version.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };

      resolve(db);
    };

    request.onerror = () => {
      dbPromise = null;
      reject(new OfflineStorageError("Could not open the device database.", request.error));
    };

    request.onblocked = () => {
      // Another tab holds an older version open. Not fatal on its own — that tab
      // will close eventually — but worth naming, because it looks like a hang.
      console.warn("Offline database upgrade is blocked by another open tab.");
    };
  });

  // A failed open must not be cached as a permanent verdict.
  dbPromise.catch(() => {
    dbPromise = null;
  });

  return dbPromise;
}

export interface TransactionOptions {
  /**
   * `true` waits for the OS to flush before reporting success. Use it for
   * anything whose loss would cost real work or real money (the outbox); leave
   * it off for cached server data, which can simply be downloaded again.
   */
  durable?: boolean;
}

/**
 * Run `work` inside one transaction and resolve only when it has COMMITTED.
 *
 * Resolving on the request callback instead of `oncomplete` is the classic
 * IndexedDB mistake: the callback fires while the transaction is still open, so
 * "saved" can be reported for a transaction that then aborts. Every write in
 * offline mode reports success only after the commit.
 */
export async function withTransaction<T>(
  stores: OfflineStoreName | OfflineStoreName[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => T | Promise<T>,
  options: TransactionOptions = {},
): Promise<T> {
  const db = await openOfflineDb();
  const names = Array.isArray(stores) ? stores : [stores];

  return new Promise<T>((resolve, reject) => {
    let tx: IDBTransaction;

    try {
      tx = options.durable
        // `durability` is not in every browser's lib.dom typings yet, and an
        // unknown option is ignored rather than fatal, so the cast is safe.
        ? db.transaction(names, mode, { durability: "strict" } as IDBTransactionOptions)
        : db.transaction(names, mode);
    }
    catch (error) {
      reject(new OfflineStorageError("Could not start a device database transaction.", error));

      return;
    }

    let result: T;
    let settled = false;

    tx.oncomplete = () => {
      if (!settled) {
        settled = true;
        resolve(result);
      }
    };

    tx.onerror = () => {
      if (!settled) {
        settled = true;
        reject(new OfflineStorageError(describeTransactionFailure(tx.error), tx.error));
      }
    };

    tx.onabort = () => {
      if (!settled) {
        settled = true;
        reject(new OfflineStorageError(describeTransactionFailure(tx.error), tx.error));
      }
    };

    void (async () => {
      try {
        result = await work(tx);
      }
      catch (error) {
        if (!settled) {
          settled = true;

          try {
            tx.abort();
          }
          catch {
            // Already finishing; the handlers above have it.
          }

          reject(error instanceof OfflineStorageError
            ? error
            : new OfflineStorageError("A device database write failed.", error));
        }
      }
    })();
  });
}

function describeTransactionFailure(error: DOMException | null): string {
  if (error?.name === "QuotaExceededError")
    return "This device has run out of storage space, so nothing was saved.";

  return "A device database write failed.";
}

/** Promisify one IDBRequest. Only ever awaited INSIDE an open transaction. */
function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new OfflineStorageError("A device database request failed.", request.error));
  });
}

export async function idbGet<T>(store: OfflineStoreName, key: IDBValidKey): Promise<T | undefined> {
  return withTransaction(store, "readonly", tx => promisifyRequest<T | undefined>(tx.objectStore(store).get(key)));
}

export async function idbGetAll<T>(store: OfflineStoreName, query?: IDBValidKey | IDBKeyRange): Promise<T[]> {
  return withTransaction(store, "readonly", tx =>
    promisifyRequest<T[]>(tx.objectStore(store).getAll(query as IDBValidKey | IDBKeyRange | null | undefined)));
}

export async function idbGetAllByIndex<T>(
  store: OfflineStoreName,
  index: string,
  query: IDBValidKey | IDBKeyRange,
): Promise<T[]> {
  return withTransaction(store, "readonly", tx =>
    promisifyRequest<T[]>(tx.objectStore(store).index(index).getAll(query)));
}

export async function idbCount(store: OfflineStoreName, query?: IDBValidKey | IDBKeyRange): Promise<number> {
  return withTransaction(store, "readonly", tx =>
    promisifyRequest<number>(tx.objectStore(store).count(query as IDBValidKey | IDBKeyRange | undefined)));
}

export async function idbPut<T>(store: OfflineStoreName, value: T, options?: TransactionOptions): Promise<void> {
  await withTransaction(store, "readwrite", async (tx) => {
    await promisifyRequest(tx.objectStore(store).put(value as unknown as any));
  }, options);
}

/** One transaction for the whole batch: a half-written pack is worse than none. */
export async function idbPutMany<T>(store: OfflineStoreName, values: T[], options?: TransactionOptions): Promise<void> {
  if (!values.length)
    return;

  await withTransaction(store, "readwrite", async (tx) => {
    const objectStore = tx.objectStore(store);

    for (const value of values)
      await promisifyRequest(objectStore.put(value as unknown as any));
  }, options);
}

export async function idbDelete(store: OfflineStoreName, key: IDBValidKey, options?: TransactionOptions): Promise<void> {
  await withTransaction(store, "readwrite", async (tx) => {
    await promisifyRequest(tx.objectStore(store).delete(key));
  }, options);
}

export async function idbClear(store: OfflineStoreName): Promise<void> {
  await withTransaction(store, "readwrite", async (tx) => {
    await promisifyRequest(tx.objectStore(store).clear());
  });
}

export interface StorageEstimateSummary {
  supported: boolean;
  usageBytes: number;
  quotaBytes: number;
  /** 0-1. `0` when the browser will not say. */
  usedRatio: number;
  /** True when we are close enough to the cap to warn a human. */
  nearlyFull: boolean;
}

const NEARLY_FULL_RATIO = 0.85;

export async function estimateStorage(): Promise<StorageEstimateSummary> {
  const empty: StorageEstimateSummary = {
    supported: false,
    usageBytes: 0,
    quotaBytes: 0,
    usedRatio: 0,
    nearlyFull: false,
  };

  if (typeof navigator === "undefined" || !navigator.storage?.estimate)
    return empty;

  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    const usedRatio = quota > 0 ? usage / quota : 0;

    return {
      supported: true,
      usageBytes: usage,
      quotaBytes: quota,
      usedRatio,
      nearlyFull: usedRatio >= NEARLY_FULL_RATIO,
    };
  }
  catch {
    return empty;
  }
}

/**
 * Ask the browser not to evict this data under storage pressure.
 *
 * Only available in a secure context, so it is a no-op on the plain-HTTP LAN
 * deployment — one more reason tills should run the desktop app or HTTPS. It is
 * a request, not a guarantee, and a refusal is not an error.
 */
export async function requestStoragePersistence(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.persist)
    return false;

  try {
    if (await navigator.storage.persisted?.())
      return true;

    return await navigator.storage.persist();
  }
  catch {
    return false;
  }
}

/** Test seam: drop the cached connection so the next call reopens. */
export function resetOfflineDbHandle(): void {
  dbPromise = null;
}
